/**
 * First-run onboarding checklist for a fresh family.
 *
 * Renders on the Dashboard, above the endpoints list, while any step is
 * still incomplete. Step state comes from the server, so a fresh browser
 * (or one whose storage was cleared) sees the same progress as any other.
 * The test step also counts as done once any alert has arrived — a device
 * that has already alerted has proven the pipeline. Dismisses to hidden
 * once every step is done — or the parent hits the "Hide checklist" link
 * explicitly.
 *
 * Steps:
 *   1. Enroll a device
 *   2. Set your BYOK API key
 *   3. Choose your notification channels
 *   4. Send yourself a test alert
 *
 * Subscribing is deliberately NOT in the checklist — the parent has 14
 * days of trial and the BillingBanner nudges them separately.
 */

import { useEffect, useState } from 'preact/hooks';
import { CheckCircle2, Circle } from 'lucide-preact';
import { Card } from './Card';
import { endpoints } from '../data/endpoints';
import { fetchHasAnyAlert } from '../data/alerts';
import { fetchByokHasKey } from '../data/byok';
import {
  getNotificationPrefs,
  type NotificationPrefs,
} from '../data/notifications';

const DISMISS_KEY = 'backstop-onboarding-dismissed';
const TEST_SENT_KEY = 'backstop-onboarding-test-sent';

interface Step {
  label: string;
  href: string;
  done: boolean;
}

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

function readTestSent(): boolean {
  try {
    return localStorage.getItem(TEST_SENT_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Mark the "test alert" step complete. Callable from elsewhere in the
 * app (SettingsRoute's Send test button, ideally) — for MVP we set it
 * from a localStorage sniff since wiring cross-component setters would
 * bloat the panel.
 */
export function markOnboardingTestSent(): void {
  try {
    localStorage.setItem(TEST_SENT_KEY, '1');
  } catch {
    /* localStorage disabled — accept the reduced UX */
  }
}

export function OnboardingChecklist() {
  const [dismissed, setDismissed] = useState<boolean>(readDismissed());
  const [byokConfigured, setByokConfigured] = useState<boolean | null>(null);
  const [prefs, setPrefs] = useState<NotificationPrefs | null>(null);
  const [testSent, setTestSent] = useState<boolean>(readTestSent());
  const [hasAlert, setHasAlert] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [hasKey, p, anyAlert] = await Promise.all([
        fetchByokHasKey().catch(() => false),
        getNotificationPrefs().catch(() => null),
        fetchHasAnyAlert().catch(() => false),
      ]);
      if (cancelled) return;
      setByokConfigured(hasKey);
      setPrefs(p);
      setHasAlert(anyAlert);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Re-read the test-sent flag on window focus so a completed test in
  // the Settings tab reflects here without a full page reload.
  useEffect(() => {
    function onFocus(): void {
      setTestSent(readTestSent());
    }
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  if (dismissed) return null;

  const steps: Step[] = [
    {
      label: 'Enroll a device',
      href: '#add-device',
      done: endpoints.value.length > 0,
    },
    {
      label: 'Set your BYOK API key',
      href: '/settings',
      done: byokConfigured === true,
    },
    {
      label: 'Choose your notification channels',
      href: '/settings',
      done:
        prefs !== null &&
        (prefs.sms_enabled || prefs.email_enabled || prefs.push_enabled),
    },
    {
      label: 'Send yourself a test alert',
      href: '/settings',
      done: testSent || hasAlert,
    },
  ];

  const completed = steps.filter((s) => s.done).length;
  if (completed === steps.length) {
    // All done — auto-dismiss so the checklist doesn't linger.
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    return null;
  }

  function onHide(): void {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  }

  return (
    <Card class="space-y-3">
      <div class="flex items-center justify-between">
        <h2 class="text-lg font-semibold">Get set up</h2>
        <span class="font-mono text-[12px] text-surface-muted">
          {completed} / {steps.length}
        </span>
      </div>
      <p class="text-sm text-surface-muted">
        A few steps to finish before Backstop is fully useful.
      </p>
      <ol class="space-y-2">
        {steps.map((s, i) => (
          <li key={i} class="flex items-center gap-3">
            {s.done ? (
              <CheckCircle2 size={18} class="text-emerald-600" aria-hidden />
            ) : (
              <Circle size={18} class="text-surface-muted" aria-hidden />
            )}
            {s.done ? (
              <span class="text-sm text-surface-muted line-through">
                {s.label}
              </span>
            ) : (
              <a href={s.href} class="text-sm underline hover:text-signal">
                {s.label}
              </a>
            )}
          </li>
        ))}
      </ol>
      <div class="pt-1">
        <button
          type="button"
          class="text-[12px] text-surface-muted underline hover:text-surface-fg"
          onClick={onHide}
        >
          Hide checklist
        </button>
      </div>
    </Card>
  );
}
